import { useTranslation } from "react-i18next";
import {
  useReferenceValueLabel,
  useRiskProfiles,
  useTimeframes,
} from "../../../api/hooks/useReference";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import { AltSymbolIcon, DirectionIcon, RiskIcon } from "@scryerlabs/ui";
import { StrategyTickers } from "./StrategyTickers";

/**
 * The dark strip across the top of a card: what the strategy trades on the left, what kind of
 * strategy it is on the right.
 *
 * Every card has one, whether or not it trades a pair — a grid where some carry a dark strip and
 * some do not reads as two designs rather than one design with a variable.
 *
 * The two groups compete for one line, and the left one is the one that can grow, so the tickers
 * truncate and the icons hold their place rather than wrapping away from the name they describe.
 */
export function StrategyIdentityBand({ strategy }: { strategy: TradingStrategyDto }) {
  const { t } = useTranslation("tradingStrategies");
  const riskProfileLabel = useReferenceValueLabel(useRiskProfiles().data);
  const timeframeLabel = useReferenceValueLabel(useTimeframes().data);

  const direction = strategy.direction ?? 0;

  return (
    <div className="strategy-band d-flex align-items-center justify-content-between gap-2">
      {/* The timeframe holds its width beside the tickers, which truncate first. */}
      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
        <StrategyTickers strategy={strategy} />
        <span className="strategy-band-timeframe" title="Timeframe">
          {timeframeLabel(strategy.timeframe)}
        </span>
      </div>

      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        {direction > 0 && (
          <DirectionIcon direction={direction} label={t(`traits.direction.${direction}`)} />
        )}
        {strategy.usesAltSymbols && <AltSymbolIcon label={t("traits.altSymbols")} />}
        <RiskIcon
          riskProfile={strategy.riskProfile}
          label={t("traits.risk", { level: riskProfileLabel(strategy.riskProfile) })}
        />
      </div>
    </div>
  );
}
